import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_PARTICIPANT_LISTINGS_PER_PAGE,
  fetchParticipantListingsPerPage,
  normalizeParticipantListingsPerPage,
  readCachedParticipantListingsPerPage,
  saveParticipantListingsPerPage,
  writeCachedParticipantListingsPerPage,
} from "Utils/userUiPreferences";

/**
 * Shared per-user page-size preference for Participant / COS / AH listings.
 *
 * Performance:
 * - Hydrates instantly from per-user local cache (no list delay).
 * - Syncs from server in the background (does not block listing queries).
 * - Persist is fire-and-forget; skips no-op PUTs.
 * - `remoteSyncToken` bumps only when server returns a different size so
 *   callers can optionally refetch once — never gates the initial load.
 */
export default function useParticipantListingsPerPage() {
  const cached = readCachedParticipantListingsPerPage();
  const initial = cached ?? DEFAULT_PARTICIPANT_LISTINGS_PER_PAGE;

  const [pageSize, setPageSize] = useState(initial);
  const [remoteSyncToken, setRemoteSyncToken] = useState(0);
  const pageSizeRef = useRef(initial);
  const lastSavedRef = useRef(initial);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const size = await fetchParticipantListingsPerPage();
      if (cancelled) return;

      lastSavedRef.current = size;
      writeCachedParticipantListingsPerPage(size);

      if (size !== pageSizeRef.current) {
        pageSizeRef.current = size;
        setPageSize(size);
        // Signal one optional list refresh; does not delay first paint/fetch.
        setRemoteSyncToken((token) => token + 1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const changePageSize = useCallback((rawSize) => {
    const next = normalizeParticipantListingsPerPage(
      rawSize && typeof rawSize === "object" ? rawSize.value : rawSize
    );

    if (next === pageSizeRef.current) {
      return next;
    }

    pageSizeRef.current = next;
    setPageSize(next);

    if (next !== lastSavedRef.current) {
      lastSavedRef.current = next;
      void saveParticipantListingsPerPage(next);
    }

    return next;
  }, []);

  return {
    pageSize,
    pageSizeRef,
    remoteSyncToken,
    changePageSize,
  };
}
